package org.gymly.ui.admin

import androidx.compose.material3.minimumInteractiveComponentSize
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.ExpandMore
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import coil3.compose.AsyncImage
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.data.persistStaffPhotoAndSync
import org.gymly.model.AdminCatalogService
import org.gymly.model.AdminClientStatus
import org.gymly.model.AdminPersonSex
import org.gymly.model.AdminStaffRole
import org.gymly.model.AdminWeekday
import org.gymly.model.AdminWorkDaySchedule
import org.gymly.model.AdminWorkTimeBlock
import org.gymly.model.AdminWorker
import org.gymly.model.AdminWorkerUpsert
import org.gymly.model.PickedImagePayload
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.util.imagePreviewModel
import org.gymly.util.isValidEmailOrBlank
import org.gymly.util.openImagePicker
import org.gymly.util.userMessage
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.ui.components.PasswordRules
import org.gymly.ui.components.PasswordRulesHint
import org.gymly.ui.components.PasswordVisibilityToggle
import org.gymly.ui.components.rememberPasswordVisible

private fun displayWorkTime(raw: String?): String = raw?.trim()?.take(5).orEmpty()

private fun normalizeWorkTimeInput(raw: String): String? {
    val t = raw.trim()
    if (t.isEmpty()) return null
    val hhmm = t.take(5)
    if (!Regex("""^\d{2}:\d{2}$""").matches(hhmm)) return null
    val h = hhmm.substring(0, 2).toIntOrNull() ?: return null
    val m = hhmm.substring(3, 5).toIntOrNull() ?: return null
    if (h !in 0..23 || m !in 0..59) return null
    return "$hhmm:00"
}

private fun workTimeToMinutes(hhmm: String): Int? {
    val t = hhmm.trim().take(5)
    if (!Regex("""^\d{2}:\d{2}$""").matches(t)) return null
    val h = t.substring(0, 2).toIntOrNull() ?: return null
    val m = t.substring(3, 5).toIntOrNull() ?: return null
    if (h !in 0..23 || m !in 0..59) return null
    return h * 60 + m
}

private fun minutesToWorkTime(total: Int): String {
    val h = (total / 60).coerceIn(0, 23)
    val m = (total % 60).coerceIn(0, 59)
    return "${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}"
}

/** Opciones HH:mm cada 30 min dentro de las franjas de Empresa del día (bloques personalizados permitidos). */
private fun workTimeOptionsForCompanyDay(
    companyDay: AdminWorkDaySchedule?,
    stepMinutes: Int = 30,
): List<String> {
    val options = linkedSetOf<String>()
    val blocks = companyDay?.blocks.orEmpty()
    if (blocks.isEmpty()) {
        var m = 0
        while (m < 24 * 60) {
            options.add(minutesToWorkTime(m))
            m += stepMinutes
        }
        return options.toList()
    }
    for (block in blocks) {
        val start = workTimeToMinutes(displayWorkTime(block.startTime)) ?: continue
        val end = workTimeToMinutes(displayWorkTime(block.endTime)) ?: continue
        if (end < start) continue
        var cursor = start
        while (cursor <= end) {
            options.add(minutesToWorkTime(cursor))
            cursor += stepMinutes
        }
        options.add(minutesToWorkTime(end))
    }
    return options.sortedBy { workTimeToMinutes(it) ?: 0 }
}

private data class WorkBlockDraft(val start: String = "", val end: String = "")

private data class WorkDayDraft(
    val enabled: Boolean = false,
    val blocks: List<WorkBlockDraft> = emptyList(),
)

private fun blocksFromCompanyDay(companyDay: AdminWorkDaySchedule): List<WorkBlockDraft> =
    companyDay.blocks.map { b ->
        WorkBlockDraft(
            start = displayWorkTime(b.startTime),
            end = displayWorkTime(b.endTime),
        )
    }.filter { it.start.isNotBlank() && it.end.isNotBlank() }

private fun initialWorkDayDrafts(schedule: List<AdminWorkDaySchedule>?): Map<Int, WorkDayDraft> {
    val byDay = schedule.orEmpty().associateBy { it.weekday }
    return AdminWeekday.entries.associate { day ->
        val existing = byDay[day.iso]
        day.iso to if (existing == null || existing.blocks.isEmpty()) {
            WorkDayDraft()
        } else {
            WorkDayDraft(
                enabled = true,
                blocks = existing.blocks.map {
                    WorkBlockDraft(
                        start = displayWorkTime(it.startTime),
                        end = displayWorkTime(it.endTime),
                    )
                }.filter { it.start.isNotBlank() && it.end.isNotBlank() },
            )
        }
    }
}

private enum class WorkersDateFilter(val label: String, val days: Int?) {
    Days7("7 días", 7),
    Days30("30 días", 30),
    All("Todos", null),
}

private sealed class WorkersDialog {
    data object Create : WorkersDialog()
    data class Edit(val worker: AdminWorker) : WorkersDialog()
    data class Detail(val worker: AdminWorker) : WorkersDialog()
    data class Delete(val worker: AdminWorker) : WorkersDialog()
}

private data class WorkerFormSubmit(
    val upsert: AdminWorkerUpsert,
    val photo: PickedImagePayload? = null,
    val removePhoto: Boolean = false,
)

@Composable
fun AdminWorkersScreen() {
    val scope = rememberCoroutineScope()
    var workers by remember { mutableStateOf<List<AdminWorker>>(emptyList()) }
    var companyId by remember { mutableStateOf<String?>(null) }
    var companyHours by remember { mutableStateOf<List<AdminWorkDaySchedule>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var dateFilter by remember { mutableStateOf(WorkersDateFilter.All) }
    var dialog by remember { mutableStateOf<WorkersDialog?>(null) }
    var saving by remember { mutableStateOf(false) }
    var dialogError by remember { mutableStateOf<String?>(null) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                workers = AdminRepo.api.listWorkers()
                val company = runCatching { AdminRepo.api.getCompany() }.getOrNull()
                companyId = company?.id ?: AdminRepo.api.listBranches().firstOrNull()?.id
                companyHours = company?.operatingHours.orEmpty()
            } catch (e: Exception) {
                error = e.userMessage("No se pudieron cargar los trabajadores.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(LocalAdminDataRevision.current) { reload() }

    val filtered = remember(workers, query, dateFilter) {
        val q = query.trim().lowercase()
        val cutoff = dateFilter.days?.let { Clock.System.now() - it.days }
        workers.filter { w ->
            val matchesQuery = q.isEmpty() ||
                w.displayName.lowercase().contains(q) ||
                (w.email?.lowercase()?.contains(q) == true) ||
                w.role.labelEs().lowercase().contains(q)
            val matchesDate = if (cutoff == null) {
                true
            } else {
                val created = w.createdAt?.let { runCatching { Instant.parse(it) }.getOrNull() }
                created == null || created >= cutoff
            }
            matchesQuery && matchesDate
        }
    }

    val listPaging = rememberAdminListPaging(
        initialSortKey = "name",
        resetKey = listOf(query, dateFilter),
    )
    val pageItems = remember(filtered, listPaging.sort, listPaging.page) {
        filtered.adminSortedPaged(listPaging.sort, listPaging.page) { key ->
            when (key) {
                "email" -> { w -> w.email?.lowercase() }
                "role" -> { w -> w.role.labelEs().lowercase() }
                "status", "active" -> { w -> if (w.active) 1 else 0 }
                else -> { w -> w.displayName.lowercase() }
            }
        }
    }

    val total = filtered.size
    val admins = filtered.count { it.role == AdminStaffRole.ADMIN }
    val trainers = filtered.count { it.role == AdminStaffRole.TRAINER }
    val active = filtered.count { it.active }

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface)
            .padding(GymlySpace.lg),
    ) {
        val compact = maxWidth < 700.dp
        // Scroll de página (como Ventas): la tabla crece hasta 20 filas sin viewport interno corto.
        VerticalScrollColumn(Modifier.fillMaxSize()) {
            WorkersHeader(dateFilter = dateFilter, onFilterChange = { dateFilter = it })
            Spacer(Modifier.height(16.dp))
            WorkersKpiRow(total, admins, trainers, active, compact)
            Spacer(Modifier.height(16.dp))
            error?.let {
                Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
            }
            WorkersDirectoryCard(
                workers = pageItems,
                totalCount = filtered.size,
                listPaging = listPaging,
                loading = loading,
                query = query,
                onQueryChange = { query = it },
                compact = compact,
                onCreate = { dialog = WorkersDialog.Create },
                onView = { dialog = WorkersDialog.Detail(it) },
                onEdit = { dialog = WorkersDialog.Edit(it) },
                onDelete = { dialog = WorkersDialog.Delete(it) },
            )
        }
    }

    LaunchedEffect(dialog) {
        dialogError = null
    }

    when (val d = dialog) {
        is WorkersDialog.Detail -> WorkerDetailDialog(d.worker) { dialog = null }
        is WorkersDialog.Create -> WorkerFormDialog(
            title = "Agregar trabajador",
            initial = null,
            companyId = companyId,
            companyHours = companyHours,
            saving = saving,
            externalError = dialogError,
            onDismiss = { if (!saving) dialog = null },
            onSave = { submit ->
                scope.launch {
                    saving = true
                    error = null
                    dialogError = null
                    try {
                        val created = AdminRepo.api.createWorker(submit.upsert)
                        if (submit.photo != null || submit.removePhoto) {
                            AdminRepo.api.persistStaffPhotoAndSync(
                                created.id,
                                photo = submit.photo,
                                removePhoto = submit.removePhoto,
                            )
                        } else {
                            runCatching { AdminRepo.api.syncStaffAccess(created.id) }
                        }
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        dialogError = e.userMessage("No se pudo guardar el trabajador.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is WorkersDialog.Edit -> WorkerFormDialog(
            title = "Editar trabajador",
            initial = d.worker,
            companyId = companyId,
            companyHours = companyHours,
            saving = saving,
            externalError = dialogError,
            onDismiss = { if (!saving) dialog = null },
            onSave = { submit ->
                scope.launch {
                    saving = true
                    error = null
                    dialogError = null
                    try {
                        AdminRepo.api.updateWorker(d.worker.id, submit.upsert)
                        if (submit.photo != null || submit.removePhoto) {
                            AdminRepo.api.persistStaffPhotoAndSync(
                                d.worker.id,
                                photo = submit.photo,
                                removePhoto = submit.removePhoto,
                            )
                        } else {
                            runCatching { AdminRepo.api.syncStaffAccess(d.worker.id) }
                        }
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        dialogError = e.userMessage("No se pudo guardar el trabajador.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is WorkersDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text("Eliminar trabajador") },
            text = {
                Text("¿Seguro que deseas eliminar a ${d.worker.displayName}? Esta acción no se puede deshacer.")
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deleteWorker(d.worker.id)
                                dialog = null
                                reload()
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo eliminar el trabajador.")
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Eliminar")
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
            },
        )
        null -> Unit
    }
}

@Composable
private fun WorkersHeader(
    dateFilter: WorkersDateFilter,
    onFilterChange: (WorkersDateFilter) -> Unit,
) {
    var menuOpen by remember { mutableStateOf(false) }
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top,
    ) {
        Column(Modifier.weight(1f)) {
            Text("Trabajadores", fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
            Text(
                "Cuentas de personal y roles desde /api/workers (nombre, correo, rol, activo).",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
        Box {
            OutlinedButton(onClick = { menuOpen = true }, shape = RoundedCornerShape(10.dp)) {
                Text(dateFilter.label, color = AdminColors.Text)
            }
            DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                WorkersDateFilter.entries.forEach { opt ->
                    DropdownMenuItem(
                        text = { Text(opt.label) },
                        onClick = {
                            onFilterChange(opt)
                            menuOpen = false
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun WorkersKpiRow(
    total: Int,
    admins: Int,
    trainers: Int,
    active: Int,
    compact: Boolean,
) {
    val items = listOf(
        Triple("Total de trabajadores", total.toString(), AdminColors.Primary),
        Triple("Administradores", admins.toString(), AdminColors.Primary),
        Triple("Entrenadores", trainers.toString(), Color(0xFF2E7D32)),
        Triple("Activos", active.toString(), Color(0xFF7C3AED)),
    )
    if (compact) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            items.chunked(2).forEach { row ->
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                    row.forEach { (label, value, accent) ->
                        WorkerKpiCard(label, value, accent, Modifier.weight(1f))
                    }
                }
            }
        }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            items.forEach { (label, value, accent) ->
                WorkerKpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun WorkerKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(6.dp))
        Text(value, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = accent)
    }
}

@Composable
private fun WorkersDirectoryCard(
    workers: List<AdminWorker>,
    totalCount: Int,
    listPaging: AdminListPaging,
    loading: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    compact: Boolean,
    onCreate: () -> Unit,
    onView: (AdminWorker) -> Unit,
    onEdit: (AdminWorker) -> Unit,
    onDelete: (AdminWorker) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("Directorio de personal", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                WorkerSearchField(query, onQueryChange)
                if (canWriteModule(AdminModuleAccess.WORKERS)) Button(
                    onClick = onCreate,
                    modifier = Modifier.fillMaxWidth().height(44.dp),
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("+ Nuevo trabajador", fontWeight = FontWeight.SemiBold)
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Directorio de personal", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.widthIn(max = 220.dp)) {
                        WorkerSearchField(query, onQueryChange)
                    }
                    if (canWriteModule(AdminModuleAccess.WORKERS)) Button(
                        onClick = onCreate,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text("+ Nuevo trabajador", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> Text("No hay trabajadores todavía.", color = AdminColors.TextMuted)
            compact -> {
                Column(
                    Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    workers.forEach { w -> WorkerMobileCard(w, onView, onEdit, onDelete) }
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
            else -> {
                WorkersTable(
                    workers,
                    listPaging = listPaging,
                    onView,
                    onEdit,
                    onDelete,
                    modifier = Modifier.fillMaxWidth(),
                )
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
        }
    }
}

@Composable
private fun WorkerSearchField(query: String, onQueryChange: (String) -> Unit) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text("Buscar...", color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

@Composable
private fun WorkersTable(
    workers: List<AdminWorker>,
    listPaging: AdminListPaging,
    onView: (AdminWorker) -> Unit,
    onEdit: (AdminWorker) -> Unit,
    onDelete: (AdminWorker) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            AdminSortableHeader("Trabajador", "name", listPaging.sort, listPaging::onSort, Modifier.weight(1.5f))
            AdminSortableHeader("Correo", "email", listPaging.sort, listPaging::onSort, Modifier.weight(1.3f))
            AdminSortableHeader("Rol", "role", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
            AdminSortableHeader("Estado", "status", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
            AdminListHeaderLabel("Acciones", Modifier.width(140.dp))
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
        workers.forEach { w ->
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 10.dp, horizontal = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Row(Modifier.weight(1.5f), verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            Modifier
                                .size(8.dp)
                                .clip(CircleShape)
                                .background(Color(w.role.dotColorArgb())),
                        )
                        Spacer(Modifier.width(10.dp))
                        Text(w.displayName, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                    }
                    Text(w.email ?: "—", Modifier.weight(1.3f), color = AdminColors.TextMuted, fontSize = 12.sp, maxLines = 1)
                    Text(w.role.labelEs(), Modifier.weight(0.9f), color = AdminColors.Text, fontSize = 13.sp, maxLines = 1)
                    Box(Modifier.weight(0.8f)) { ActiveBadge(w.active) }
                    Row(Modifier.width(140.dp)) {
                        IconButton(onClick = { onView(w) }) {
                            Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
                        }
                        if (canWriteModule(AdminModuleAccess.WORKERS)) IconButton(onClick = { onEdit(w) }) {
                            Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.Primary)
                        }
                        if (canDeleteModule(AdminModuleAccess.WORKERS)) IconButton(onClick = { onDelete(w) }) {
                            Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
                        }
                    }
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
        }
    }
}

@Composable
private fun WorkerMobileCard(
    worker: AdminWorker,
    onView: (AdminWorker) -> Unit,
    onEdit: (AdminWorker) -> Unit,
    onDelete: (AdminWorker) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.6f), RoundedCornerShape(12.dp))
            .padding(14.dp),
    ) {
        Text(worker.displayName, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
        Text(worker.email ?: "—", color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(8.dp))
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
            Text(worker.role.labelEs(), color = AdminColors.Text, fontSize = 12.sp)
            ActiveBadge(worker.active)
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            IconButton(onClick = { onView(worker) }) {
                Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted)
            }
            if (canWriteModule(AdminModuleAccess.WORKERS)) IconButton(onClick = { onEdit(worker) }) {
                Icon(Icons.Outlined.Edit, null, tint = AdminColors.Primary)
            }
            if (canDeleteModule(AdminModuleAccess.WORKERS)) IconButton(onClick = { onDelete(worker) }) {
                Icon(Icons.Outlined.Delete, null, tint = AdminColors.Error)
            }
        }
    }
}

@Composable
private fun WorkerRoleField(role: AdminStaffRole, onRoleChange: (AdminStaffRole) -> Unit) {
    var menu by remember { mutableStateOf(false) }
    WorkerFormLabel("Rol")
    Box {
        OutlinedButton(onClick = { menu = true }, modifier = Modifier.fillMaxWidth()) {
            Text(role.labelEs(), color = AdminColors.Text)
        }
        DropdownMenu(expanded = menu, onDismissRequest = { menu = false }) {
            AdminStaffRole.entries.forEach { r ->
                DropdownMenuItem(
                    text = { Text(r.labelEs()) },
                    onClick = {
                        onRoleChange(r)
                        menu = false
                    },
                )
            }
        }
    }
}

@Composable
private fun ActiveBadge(active: Boolean) {
    val bg = if (active) Color(0xFFE8F5E9) else Color(0xFFF3F4F6)
    val fg = if (active) Color(0xFF2E7D32) else AdminColors.TextMuted
    Text(
        if (active) "Activo" else "Inactivo",
        color = fg,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(bg)
            .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}

@Composable
private fun WorkerDetailDialog(worker: AdminWorker, onDismiss: () -> Unit) {
    var photoBytes by remember(worker.id) { mutableStateOf<ByteArray?>(null) }
    LaunchedEffect(worker.id, worker.photoUrl) {
        photoBytes = if (!worker.photoUrl.isNullOrBlank()) {
            runCatching { AdminRepo.api.fetchWorkerPhotoBytes(worker.id) }.getOrNull()
        } else {
            null
        }
    }
    val previewModel = imagePreviewModel(photoBytes)
    val initials = "${worker.firstName.firstOrNull() ?: '?'}${worker.lastName.firstOrNull() ?: ""}".uppercase()

    Dialog(onDismissRequest = onDismiss) {
        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            VerticalScrollColumn(
                Modifier
                    .widthIn(max = 560.dp)
                    .heightIn(max = 720.dp)
                    .fillMaxWidth(0.95f)
                    .clip(RoundedCornerShape(16.dp))
                    .background(AdminColors.SurfaceContainer)
                    .padding(16.dp),
            ) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text("Detalle del trabajador", fontWeight = FontWeight.Bold, fontSize = 18.sp)
                Icon(
                    Icons.Outlined.Close,
                    contentDescription = "Cerrar",
                    tint = AdminColors.TextMuted,
                    modifier = Modifier.minimumInteractiveComponentSize().clickable(onClick = onDismiss).size(22.dp),
                )
            }
            Spacer(Modifier.height(12.dp))

            Row(
                Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                Box(
                    Modifier
                        .size(64.dp)
                        .clip(CircleShape)
                        .background(AdminColors.Outline.copy(alpha = 0.25f))
                        .border(1.dp, AdminColors.Outline.copy(alpha = 0.45f), CircleShape),
                    contentAlignment = Alignment.Center,
                ) {
                    if (previewModel != null) {
                        key(previewModel.takeLast(32)) {
                            AsyncImage(
                                model = previewModel,
                                contentDescription = "Foto del trabajador",
                                modifier = Modifier.fillMaxSize(),
                                contentScale = ContentScale.Crop,
                            )
                        }
                    } else {
                        Text(initials, fontWeight = FontWeight.Bold, fontSize = 28.sp, color = AdminColors.Primary)
                    }
                }
                Column(Modifier.weight(1f)) {
                    Text(worker.displayName, fontWeight = FontWeight.Bold, fontSize = 17.sp, color = AdminColors.Text)
                    Text(worker.email ?: "—", color = AdminColors.TextMuted, fontSize = 13.sp)
                    Spacer(Modifier.height(6.dp))
                    ActiveBadge(worker.active)
                }
            }

            Spacer(Modifier.height(16.dp))
            // Celular: datos en una columna. Escritorio: dos columnas iguales.
            BoxWithConstraints(Modifier.fillMaxWidth()) {
            EqualColumnsSplit(
                narrow = maxWidth < 600.dp,
                first = { colMod ->
                Column(colMod) {
                    WorkerDetailRow("Nombre", worker.firstName)
                    WorkerDetailRow("Correo", worker.email ?: "—")
                    WorkerDetailRow("Sexo", worker.sex?.labelEs() ?: "—")
                    WorkerDetailRow("Estado", if (worker.active) "Activo" else "Inactivo")
                    WorkerDetailRow("Sucursal", worker.branchName ?: "—")
                }
                },
                second = { colMod ->
                Column(colMod) {
                    WorkerDetailRow("Apellido", worker.lastName)
                    WorkerDetailRow("Teléfono", worker.phone ?: "—")
                    WorkerDetailRow("Documento", worker.docId ?: "—")
                    WorkerDetailRow("Dirección", worker.address ?: "—")
                    WorkerDetailRow("Rol", worker.role.labelEs())
                }
                },
            )
            }
            Spacer(Modifier.height(8.dp))
            Text(
                "Jornada laboral",
                fontWeight = FontWeight.SemiBold,
                fontSize = 14.sp,
                color = AdminColors.Text,
                modifier = Modifier.padding(bottom = 8.dp),
            )
            if (worker.workSchedule.isEmpty()) {
                Text(
                    "Sin jornada definida.",
                    color = AdminColors.TextMuted,
                    fontSize = 13.sp,
                    modifier = Modifier.padding(bottom = 8.dp),
                )
            } else {
                AdminWeekday.entries.forEach { day ->
                    val daySchedule = worker.workSchedule.firstOrNull { it.weekday == day.iso }
                    val enabled = daySchedule != null && daySchedule.blocks.isNotEmpty()
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .padding(bottom = 8.dp)
                            .clip(RoundedCornerShape(12.dp))
                            .border(
                                1.dp,
                                if (enabled) AdminColors.Primary.copy(alpha = 0.35f)
                                else AdminColors.Outline.copy(alpha = 0.35f),
                                RoundedCornerShape(12.dp),
                            )
                            .background(
                                if (enabled) AdminColors.Primary.copy(alpha = 0.04f)
                                else AdminColors.Surface.copy(alpha = 0.5f),
                            )
                            .padding(10.dp),
                    ) {
                        Text(
                            day.labelEs,
                            fontWeight = FontWeight.SemiBold,
                            fontSize = 13.sp,
                            color = AdminColors.Text,
                        )
                        Spacer(Modifier.height(4.dp))
                        if (!enabled) {
                            Text("No trabaja", color = AdminColors.TextMuted, fontSize = 12.sp)
                        } else {
                            daySchedule!!.blocks.forEach { block ->
                                Text(
                                    "${displayWorkTime(block.startTime)} – ${displayWorkTime(block.endTime)}",
                                    color = AdminColors.Text,
                                    fontSize = 13.sp,
                                    modifier = Modifier.padding(top = 2.dp),
                                )
                            }
                        }
                    }
                }
            }
            if (worker.role == AdminStaffRole.TRAINER) {
                WorkerDetailRow(
                    "Servicios",
                    if (worker.serviceIds.isEmpty()) "—" else "${worker.serviceIds.size} asignado(s)",
                )
            }
            Spacer(Modifier.height(16.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                Button(
                    onClick = onDismiss,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("Cerrar")
                }
            }
            }
        }
    }
}

@Composable
private fun WorkerDetailRow(label: String, value: String) {
    Column(Modifier.padding(bottom = 10.dp)) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, color = AdminColors.Text, fontSize = 14.sp, fontWeight = FontWeight.Medium)
    }
}

/**
 * Escritorio: dos columnas de igual alto. Celular ([narrow]): una columna debajo de la otra.
 * Cada columna recibe el modificador que debe usar.
 */
@Composable
private fun EqualColumnsSplit(
    narrow: Boolean,
    first: @Composable (Modifier) -> Unit,
    second: @Composable (Modifier) -> Unit,
) {
    if (narrow) {
        Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            first(Modifier.fillMaxWidth())
            second(Modifier.fillMaxWidth())
        }
    } else {
        Row(
            Modifier
                .fillMaxWidth()
                .height(IntrinsicSize.Max),
            horizontalArrangement = Arrangement.spacedBy(24.dp),
        ) {
            first(Modifier.weight(1f).fillMaxHeight())
            second(Modifier.weight(1f).fillMaxHeight())
        }
    }
}

@Composable
private fun WorkerFormSectionCard(
    title: String,
    modifier: Modifier = Modifier,
    content: @Composable ColumnScope.() -> Unit,
) {
    Column(
        modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(14.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.45f), RoundedCornerShape(14.dp))
            .background(AdminColors.Surface)
            .padding(14.dp),
    ) {
        Text(title, fontWeight = FontWeight.SemiBold, fontSize = 14.sp, color = AdminColors.Text)
        Spacer(Modifier.height(10.dp))
        content()
    }
}

// Servicios en línea (se acomodan en varias filas solo si no caben), cada uno con su casilla.
@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun WorkerServiceChecks(
    services: List<AdminCatalogService>,
    selectedIds: Set<String>,
    enabled: Boolean,
    onToggle: (id: String, checked: Boolean) -> Unit,
) {
    FlowRow(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(24.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        services.forEach { service ->
            val checked = service.id in selectedIds
            Row(
                Modifier
                    .clickable(enabled = enabled) { onToggle(service.id, !checked) }
                    .padding(vertical = 2.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Checkbox(
                    checked = checked,
                    onCheckedChange = { on -> onToggle(service.id, on) },
                    enabled = enabled,
                    colors = CheckboxDefaults.colors(
                        checkedColor = AdminColors.Primary,
                        checkmarkColor = AdminColors.OnBrand,
                    ),
                )
                Text(
                    "${service.name} (${service.type.labelEs()})",
                    color = AdminColors.Text,
                    fontSize = 13.sp,
                )
            }
        }
    }
}

@Composable
private fun WorkerFormDialog(
    title: String,
    initial: AdminWorker?,
    companyId: String?,
    companyHours: List<AdminWorkDaySchedule> = emptyList(),
    saving: Boolean,
    externalError: String? = null,
    onDismiss: () -> Unit,
    onSave: (WorkerFormSubmit) -> Unit,
) {
    val nameParts = remember(initial) {
        val raw = initial?.displayName.orEmpty().trim()
        val split = raw.split(" ", limit = 2)
        split.firstOrNull().orEmpty() to split.getOrNull(1).orEmpty()
    }
    var firstName by remember(initial) { mutableStateOf(nameParts.first) }
    var lastName by remember(initial) { mutableStateOf(nameParts.second) }
    var email by remember(initial) { mutableStateOf(initial?.email.orEmpty()) }
    var phone by remember(initial) { mutableStateOf(initial?.phone.orEmpty()) }
    var docId by remember(initial) { mutableStateOf(initial?.docId.orEmpty()) }
    var password by remember { mutableStateOf("") }
    var sex by remember(initial) { mutableStateOf(initial?.sex) }
    var address by remember(initial) { mutableStateOf(initial?.address.orEmpty()) }
    var status by remember(initial) {
        mutableStateOf(
            if (initial?.active == false) AdminClientStatus.INACTIVE else AdminClientStatus.ACTIVE,
        )
    }
    var inactivationReason by remember(initial) { mutableStateOf(initial?.inactivationReason) }
    var inactivationNote by remember(initial) { mutableStateOf(initial?.inactivationNote.orEmpty()) }
    var role by remember(initial) { mutableStateOf(initial?.role ?: AdminStaffRole.TRAINER) }
    var dayDrafts by remember(initial) { mutableStateOf(initialWorkDayDrafts(initial?.workSchedule)) }
    var selectedServiceIds by remember(initial) {
        mutableStateOf(initial?.serviceIds?.toSet() ?: emptySet())
    }
    var catalogServices by remember { mutableStateOf<List<AdminCatalogService>>(emptyList()) }
    val branchId = companyId
    var formError by remember { mutableStateOf<String?>(null) }
    var sexError by remember { mutableStateOf(false) }
    var addressError by remember { mutableStateOf(false) }
    var sexMenu by remember { mutableStateOf(false) }
    var statusMenu by remember { mutableStateOf(false) }
    val visibleError = formError ?: externalError
    var pendingPhoto by remember { mutableStateOf<PickedImagePayload?>(null) }
    var pendingPhotoName by remember { mutableStateOf<String?>(null) }
    var removePhoto by remember { mutableStateOf(false) }
    var existingPhotoBytes by remember { mutableStateOf<ByteArray?>(null) }
    var showTerminalCapture by remember { mutableStateOf(false) }
    val focusFirstName = remember { FocusRequester() }
    val focusLastName = remember { FocusRequester() }
    val focusEmail = remember { FocusRequester() }
    val focusPhone = remember { FocusRequester() }
    val focusDocId = remember { FocusRequester() }
    val focusAddress = remember { FocusRequester() }
    val focusPassword = remember { FocusRequester() }
    val editing = initial != null
    val trainerServices = remember(catalogServices) {
        catalogServices.filter { it.active }.sortedBy { it.name.lowercase() }
    }

    fun updateDay(iso: Int, transform: (WorkDayDraft) -> WorkDayDraft) {
        dayDrafts = dayDrafts.toMutableMap().also { it[iso] = transform(it[iso] ?: WorkDayDraft()) }
    }

    LaunchedEffect(Unit) {
        runCatching { focusFirstName.requestFocus() }
        catalogServices = runCatching { AdminRepo.api.listServices() }.getOrDefault(emptyList())
    }

    LaunchedEffect(companyHours) {
        if (companyHours.isEmpty()) return@LaunchedEffect
        val openDays = companyHours.map { it.weekday }.toSet()
        dayDrafts = dayDrafts.mapValues { (iso, draft) ->
            if (draft.enabled && iso !in openDays) draft.copy(enabled = false) else draft
        }
    }

    LaunchedEffect(initial?.id, initial?.photoUrl) {
        pendingPhoto = null
        pendingPhotoName = null
        removePhoto = false
        existingPhotoBytes = null
        val id = initial?.id
        if (id != null && !initial.photoUrl.isNullOrBlank()) {
            existingPhotoBytes = runCatching { AdminRepo.api.fetchWorkerPhotoBytes(id) }.getOrNull()
        }
    }

    fun pickPhoto() {
        openImagePicker { picked ->
            if (picked == null) return@openImagePicker
            pendingPhoto = PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType)
            pendingPhotoName = picked.fileName
            removePhoto = false
        }
    }

    fun applyCapturedPhoto(payload: PickedImagePayload) {
        pendingPhoto = payload
        pendingPhotoName = payload.fileName
        removePhoto = false
    }

    if (showTerminalCapture) {
        TerminalPhotoCaptureDialog(
            onDismiss = { showTerminalCapture = false },
            onCaptured = { applyCapturedPhoto(it) },
        )
    }

    Dialog(
        onDismissRequest = onDismiss,
        properties = androidx.compose.ui.window.DialogProperties(usePlatformDefaultWidth = false),
    ) {
        Column(
            Modifier
                .fillMaxWidth(0.92f)
                .widthIn(min = 860.dp, max = 1180.dp)
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(24.dp)
                .verticalScroll(rememberScrollState()),
        ) {
            Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Spacer(Modifier.height(14.dp))
            // Escritorio: dos columnas iguales. Celular: una columna (datos y luego jornada).
            BoxWithConstraints(Modifier.fillMaxWidth()) {
            EqualColumnsSplit(
                narrow = maxWidth < 600.dp,
                first = { colMod ->
                Column(
                    colMod,
                    verticalArrangement = Arrangement.spacedBy(12.dp),
                ) {
                    WorkerFormSectionCard("Datos personales") {
                        ClientPhotoField(
                            firstName = firstName.ifBlank { "?" },
                            lastName = lastName.ifBlank { "" },
                            pendingPhoto = pendingPhoto,
                            existingPhotoBytes = existingPhotoBytes,
                            pendingFileName = pendingPhotoName,
                            removePhoto = removePhoto,
                            enabled = !saving,
                            onPickPhoto = { pickPhoto() },
                            onCaptureFromTerminal = { showTerminalCapture = true },
                            onClearPhoto = {
                                pendingPhoto = null
                                pendingPhotoName = null
                                removePhoto = true
                            },
                        )
                        Spacer(Modifier.height(12.dp))
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                            Column(Modifier.weight(1f)) {
                                WorkerFormLabel("Nombre *")
                                WorkerFormField(
                                    firstName,
                                    { firstName = it },
                                    "Nombre",
                                    focusRequester = focusFirstName,
                                    onNext = { focusLastName.requestFocus() },
                                    onPrev = { },
                                )
                            }
                            Column(Modifier.weight(1f)) {
                                WorkerFormLabel("Apellido *")
                                WorkerFormField(
                                    lastName,
                                    { lastName = it },
                                    "Apellido",
                                    focusRequester = focusLastName,
                                    onNext = { focusEmail.requestFocus() },
                                    onPrev = { focusFirstName.requestFocus() },
                                )
                            }
                        }
                        Spacer(Modifier.height(10.dp))
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                            Column(Modifier.weight(1f)) {
                                WorkerFormLabel("Correo (opcional)")
                                WorkerFormField(
                                    email,
                                    { email = it },
                                    "correo@ejemplo.com",
                                    focusRequester = focusEmail,
                                    onNext = { focusPhone.requestFocus() },
                                    onPrev = { focusLastName.requestFocus() },
                                )
                            }
                            Column(Modifier.weight(1f)) {
                                WorkerFormLabel("Teléfono (opcional)")
                                WorkerFormField(
                                    phone,
                                    { phone = it },
                                    "Ej. +58 412 0000000",
                                    focusRequester = focusPhone,
                                    onNext = { focusDocId.requestFocus() },
                                    onPrev = { focusEmail.requestFocus() },
                                )
                            }
                        }
                        Spacer(Modifier.height(10.dp))
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                            Column(Modifier.weight(1f)) {
                                WorkerFormLabel("Sexo *")
                                Box {
                                    OutlinedButton(
                                        onClick = { sexMenu = true },
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .then(
                                                if (sexError) {
                                                    Modifier.border(1.dp, AdminColors.Error, RoundedCornerShape(10.dp))
                                                } else {
                                                    Modifier
                                                },
                                            ),
                                    ) {
                                        Text(
                                            sex?.labelEs() ?: "Seleccionar",
                                            color = if (sexError) AdminColors.Error else AdminColors.Text,
                                        )
                                    }
                                    DropdownMenu(expanded = sexMenu, onDismissRequest = { sexMenu = false }) {
                                        AdminPersonSex.entries.forEach { option ->
                                            DropdownMenuItem(
                                                text = { Text(option.labelEs()) },
                                                onClick = {
                                                    sex = option
                                                    sexError = false
                                                    if (formError == "El sexo es obligatorio.") formError = null
                                                    sexMenu = false
                                                },
                                            )
                                        }
                                    }
                                }
                                if (sexError) {
                                    Text(
                                        "Selecciona el sexo para poder guardar.",
                                        color = AdminColors.Error,
                                        fontSize = 12.sp,
                                        modifier = Modifier.padding(top = 4.dp),
                                    )
                                }
                            }
                            Column(Modifier.weight(1f)) {
                                WorkerFormLabel("Documento")
                                WorkerFormField(
                                    docId,
                                    { docId = it },
                                    "Cédula o pasaporte",
                                    focusRequester = focusDocId,
                                    onNext = { focusAddress.requestFocus() },
                                    onPrev = { focusPhone.requestFocus() },
                                )
                            }
                        }
                        Spacer(Modifier.height(10.dp))
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                            Column(Modifier.weight(1f)) {
                                WorkerFormLabel("Estado")
                                Box {
                                    OutlinedButton(onClick = { statusMenu = true }, modifier = Modifier.fillMaxWidth()) {
                                        Text(status.labelEs(), color = AdminColors.Text)
                                    }
                                    DropdownMenu(expanded = statusMenu, onDismissRequest = { statusMenu = false }) {
                                        AdminClientStatus.entries.forEach { s ->
                                            DropdownMenuItem(
                                                text = { Text(s.labelEs()) },
                                                onClick = {
                                                    status = s
                                                    statusMenu = false
                                                },
                                            )
                                        }
                                    }
                                }
                            }
                            // Inactivo: el motivo ocupa la columna derecha, al lado del Estado; el Rol pasa abajo.
                            if (status == AdminClientStatus.INACTIVE) {
                                Column(Modifier.weight(1f)) {
                                    InactivationReasonFields(
                                        reasons = STAFF_INACTIVATION_REASONS,
                                        reason = inactivationReason,
                                        onReasonChange = { inactivationReason = it },
                                        note = inactivationNote,
                                        onNoteChange = { inactivationNote = it },
                                        enabled = !saving,
                                    )
                                }
                            } else {
                                Column(Modifier.weight(1f)) {
                                    WorkerRoleField(role = role, onRoleChange = { r ->
                                        role = r
                                        if (r != AdminStaffRole.TRAINER) {
                                            selectedServiceIds = emptySet()
                                        }
                                    })
                                }
                            }
                        }
                        if (status == AdminClientStatus.INACTIVE) {
                            Spacer(Modifier.height(10.dp))
                            Column(Modifier.fillMaxWidth()) {
                                WorkerRoleField(role = role, onRoleChange = { r ->
                                    role = r
                                    if (r != AdminStaffRole.TRAINER) {
                                        selectedServiceIds = emptySet()
                                    }
                                })
                            }
                        }
                        Spacer(Modifier.height(10.dp))
                        WorkerFormLabel("Dirección *")
                        WorkerFormField(
                            address,
                            {
                                address = it
                                if (it.isNotBlank()) {
                                    addressError = false
                                    if (formError == "La dirección es obligatoria.") formError = null
                                }
                            },
                            "Calle, número, ciudad",
                            focusRequester = focusAddress,
                            isError = addressError,
                            onNext = {
                                if (editing) focusPassword.requestFocus()
                            },
                            onPrev = { focusDocId.requestFocus() },
                            imeAction = if (editing) ImeAction.Next else ImeAction.Done,
                        )
                        if (addressError) {
                            Text(
                                "La dirección es obligatoria para guardar.",
                                color = AdminColors.Error,
                                fontSize = 12.sp,
                                modifier = Modifier.padding(top = 4.dp),
                            )
                        }
                        run {
                            Spacer(Modifier.height(10.dp))
                            WorkerFormLabel(if (editing) "Nueva contraseña (opcional)" else "Contraseña *")
                            WorkerFormField(
                                password,
                                { password = it },
                                "Mínimo 8 caracteres",
                                isPassword = true,
                                focusRequester = focusPassword,
                                onNext = { },
                                onPrev = { focusAddress.requestFocus() },
                                imeAction = ImeAction.Done,
                            )
                            PasswordRulesHint(password, muted = AdminColors.TextMuted)
                            GeneratePasswordButton(
                                value = password,
                                onGenerated = { password = it },
                                onClear = { password = "" },
                            )
                            Text(
                                if (editing) "Deja en blanco para mantener la actual."
                                else "Obligatoria: el trabajador entra al web admin con esta contraseña.",
                                color = AdminColors.TextMuted,
                                fontSize = 12.sp,
                                modifier = Modifier.padding(top = 4.dp),
                            )
                        }
                    }
                }
                },
                second = { colMod ->
                Column(
                    colMod,
                    verticalArrangement = Arrangement.spacedBy(12.dp),
                ) {
                    WorkerFormSectionCard("Jornada laboral", Modifier.weight(1f)) {
                        val companyByDay = remember(companyHours) {
                            companyHours.associateBy { it.weekday }
                        }
                        if (companyHours.isEmpty()) {
                            Text(
                                "Primero configura el horario de atención en Configuración → Empresa. La disponibilidad de los trabajadores se valida contra ese horario.",
                                color = AdminColors.Error,
                                fontSize = 12.sp,
                                modifier = Modifier.padding(bottom = 8.dp),
                            )
                        } else {
                            Text(
                                "Disponibilidad del trabajador dentro del horario de la empresa: ${formatOperatingHoursHint(companyHours)}. Solo puedes activar días abiertos y bloques que quepan en esas franjas.",
                                color = AdminColors.TextMuted,
                                fontSize = 12.sp,
                                modifier = Modifier.padding(bottom = 8.dp),
                            )
                        }
                        AdminWeekday.entries.forEach { day ->
                            val draft = dayDrafts[day.iso] ?: WorkDayDraft()
                            val companyDay = companyByDay[day.iso]
                            val dayAllowed = companyDay != null && companyDay.blocks.isNotEmpty()
                            Column(
                                Modifier
                                    .fillMaxWidth()
                                    .padding(bottom = 8.dp)
                                    .clip(RoundedCornerShape(12.dp))
                                    .border(
                                        1.dp,
                                        if (draft.enabled) AdminColors.Primary.copy(alpha = 0.35f)
                                        else AdminColors.Outline.copy(alpha = 0.35f),
                                        RoundedCornerShape(12.dp),
                                    )
                                    .background(
                                        if (draft.enabled) AdminColors.Primary.copy(alpha = 0.04f)
                                        else AdminColors.SurfaceContainer.copy(alpha = 0.35f),
                                    )
                                    .padding(10.dp),
                            ) {
                                Row(
                                    Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                ) {
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Checkbox(
                                            checked = draft.enabled && dayAllowed,
                                            onCheckedChange = { on ->
                                                if (!dayAllowed) return@Checkbox
                                                updateDay(day.iso) {
                                                    // Al activar: siempre el esquema de Empresa para ese día.
                                                    // Al desactivar: limpia bloques para no reutilizar basura al reactivar.
                                                    it.copy(
                                                        enabled = on,
                                                        blocks = if (on) {
                                                            blocksFromCompanyDay(companyDay!!)
                                                        } else {
                                                            emptyList()
                                                        },
                                                    )
                                                }
                                            },
                                            enabled = !saving && dayAllowed,
                                            colors = CheckboxDefaults.colors(
                                                checkedColor = AdminColors.Primary,
                                                checkmarkColor = AdminColors.OnBrand,
                                            ),
                                        )
                                        Column {
                                            Text(day.labelEs, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                                            if (!dayAllowed) {
                                                Text(
                                                    "Cerrado en la empresa",
                                                    color = AdminColors.TextMuted,
                                                    fontSize = 12.sp,
                                                )
                                            } else {
                                                Text(
                                                    "Empresa: " + companyDay!!.blocks.joinToString(", ") {
                                                        "${displayWorkTime(it.startTime)}–${displayWorkTime(it.endTime)}"
                                                    },
                                                    color = AdminColors.TextMuted,
                                                    fontSize = 12.sp,
                                                )
                                            }
                                        }
                                    }
                                    if (draft.enabled && dayAllowed) {
                                        TextButton(
                                            onClick = {
                                                val unused = companyDay!!.blocks.firstOrNull { companyBlock ->
                                                    val start = displayWorkTime(companyBlock.startTime)
                                                    val end = displayWorkTime(companyBlock.endTime)
                                                    draft.blocks.none { it.start == start && it.end == end }
                                                } ?: companyDay.blocks.lastOrNull()
                                                updateDay(day.iso) {
                                                    it.copy(
                                                        blocks = it.blocks + WorkBlockDraft(
                                                            start = displayWorkTime(unused?.startTime),
                                                            end = displayWorkTime(unused?.endTime),
                                                        ),
                                                    )
                                                }
                                            },
                                            enabled = !saving,
                                        ) {
                                            Text("+ Bloque", color = AdminColors.Primary, fontSize = 12.sp)
                                        }
                                    }
                                }
                                if (draft.enabled && dayAllowed) {
                                    val timeOptions = workTimeOptionsForCompanyDay(companyDay)
                                    draft.blocks.forEachIndexed { idx, block ->
                                        Row(
                                            Modifier.fillMaxWidth().padding(top = 6.dp),
                                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                                            verticalAlignment = Alignment.CenterVertically,
                                        ) {
                                            HybridWorkTimeField(
                                                label = "Inicio",
                                                value = block.start,
                                                options = timeOptions,
                                                enabled = !saving,
                                                modifier = Modifier.weight(1f),
                                                onValueChange = { clean ->
                                                    updateDay(day.iso) {
                                                        val next = it.blocks.toMutableList()
                                                        next[idx] = next[idx].copy(start = clean)
                                                        it.copy(blocks = next)
                                                    }
                                                },
                                            )
                                            HybridWorkTimeField(
                                                label = "Fin",
                                                value = block.end,
                                                options = timeOptions,
                                                enabled = !saving,
                                                modifier = Modifier.weight(1f),
                                                onValueChange = { clean ->
                                                    updateDay(day.iso) {
                                                        val next = it.blocks.toMutableList()
                                                        next[idx] = next[idx].copy(end = clean)
                                                        it.copy(blocks = next)
                                                    }
                                                },
                                            )
                                            if (draft.blocks.size > 1) {
                                                IconButton(
                                                    onClick = {
                                                        updateDay(day.iso) {
                                                            it.copy(blocks = it.blocks.filterIndexed { i, _ -> i != idx })
                                                        }
                                                    },
                                                    enabled = !saving,
                                                ) {
                                                    Icon(
                                                        Icons.Outlined.Close,
                                                        contentDescription = "Quitar bloque",
                                                        tint = AdminColors.TextMuted,
                                                    )
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        Text(
                            "Formato 24 h (HH:mm). Puedes escribir o elegir de la lista (cada 30 min). El bloque puede ser personalizado, pero debe caber dentro del horario de la empresa.",
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                            modifier = Modifier.padding(top = 4.dp),
                        )
                    }
                }
            },
            )
            }
            visibleError?.let { msg ->
                Text(
                    msg,
                    color = AdminColors.Error,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 12.dp)
                        .clip(RoundedCornerShape(10.dp))
                        .background(AdminColors.Error.copy(alpha = 0.10f))
                        .border(1.dp, AdminColors.Error.copy(alpha = 0.35f), RoundedCornerShape(10.dp))
                        .padding(horizontal = 12.dp, vertical = 10.dp),
                )
            }
            // Servicios a todo el ancho, debajo de las dos columnas (solo entrenadores).
            if (role == AdminStaffRole.TRAINER) {
                Spacer(Modifier.height(12.dp))
                WorkerFormSectionCard("Servicios que realiza") {
                    if (trainerServices.isEmpty()) {
                        Text(
                            "No hay servicios activos en el catálogo.",
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                        )
                    } else {
                        WorkerServiceChecks(
                            services = trainerServices,
                            selectedIds = selectedServiceIds,
                            enabled = !saving,
                        ) { id, on ->
                            selectedServiceIds = if (on) selectedServiceIds + id else selectedServiceIds - id
                        }
                    }
                }
            }
            Spacer(Modifier.height(18.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                TextButton(onClick = onDismiss, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Spacer(Modifier.width(8.dp))
                Button(
                    onClick = {
                        sexError = false
                        addressError = false
                        val first = firstName.trim()
                        val last = lastName.trim()
                        if (first.isEmpty()) {
                            formError = "El nombre es obligatorio."
                            return@Button
                        }
                        if (last.isEmpty()) {
                            formError = "El apellido es obligatorio."
                            return@Button
                        }
                        if (!email.isValidEmailOrBlank()) {
                            formError = "El correo debe incluir @ y un dominio (ej. nombre@correo.com)."
                            return@Button
                        }
                        val selectedSex = sex
                        if (selectedSex == null) {
                            sexError = true
                            formError = "Completa Sexo y Dirección (obligatorios) para guardar la ficha."
                            return@Button
                        }
                        val addr = address.trim()
                        if (addr.isEmpty()) {
                            addressError = true
                            formError = "Completa Sexo y Dirección (obligatorios) para guardar la ficha."
                            runCatching { focusAddress.requestFocus() }
                            return@Button
                        }
                        if (!editing && password.isBlank()) {
                            formError = "La contraseña es obligatoria para crear un trabajador."
                            return@Button
                        }
                        if (password.isNotBlank() && PasswordRules.unmet(password.trim()).isNotEmpty()) {
                            formError = "La contraseña debe tener al menos 8 caracteres, una letra y un número."
                            return@Button
                        }
                        val schedule = mutableListOf<AdminWorkDaySchedule>()
                        for (day in AdminWeekday.entries) {
                            val draft = dayDrafts[day.iso] ?: continue
                            if (!draft.enabled) continue
                            if (draft.blocks.isEmpty()) {
                                formError = "${day.labelEs}: agrega al menos un bloque horario."
                                return@Button
                            }
                            val blocks = mutableListOf<AdminWorkTimeBlock>()
                            for ((i, block) in draft.blocks.withIndex()) {
                                val startNorm = normalizeWorkTimeInput(block.start)
                                val endNorm = normalizeWorkTimeInput(block.end)
                                if (startNorm == null || endNorm == null) {
                                    formError = "${day.labelEs}: bloque ${i + 1} inválido. Usa HH:mm."
                                    return@Button
                                }
                                if (endNorm <= startNorm) {
                                    formError = "${day.labelEs}: en el bloque ${i + 1} el fin debe ser posterior al inicio."
                                    return@Button
                                }
                                blocks.add(AdminWorkTimeBlock(startTime = startNorm, endTime = endNorm))
                            }
                            val sorted = blocks.sortedBy { it.startTime }
                            for (i in 1 until sorted.size) {
                                if (sorted[i].startTime < sorted[i - 1].endTime) {
                                    formError = "${day.labelEs}: los bloques no pueden solaparse."
                                    return@Button
                                }
                            }
                            schedule.add(AdminWorkDaySchedule(weekday = day.iso, blocks = sorted))
                        }
                        validateWithinCompanyHours(schedule, companyHours)?.let {
                            formError = it
                            return@Button
                        }
                        val inactive = status == AdminClientStatus.INACTIVE
                        if (inactive && inactivationReason == null) {
                            formError = "Indica el motivo de la inactivación."
                            return@Button
                        }
                        if (inactive && inactivationReason == "OTRO" && inactivationNote.isBlank()) {
                            formError = "Describe el motivo de la inactivación."
                            return@Button
                        }
                        formError = null
                        onSave(
                            WorkerFormSubmit(
                                upsert = AdminWorkerUpsert(
                                    displayName = "$first $last".trim(),
                                    email = email.trim().ifBlank { null },
                                    phone = phone.trim().ifBlank { null },
                                    docId = docId.trim().ifBlank { null },
                                    role = role,
                                    password = password.trim().takeIf { it.isNotEmpty() },
                                    active = status == AdminClientStatus.ACTIVE,
                                    inactivationReason = if (inactive) inactivationReason else null,
                                    inactivationNote = if (inactive) inactivationNote.trim().ifBlank { null } else null,
                                    branchId = branchId,
                                    sex = selectedSex,
                                    address = addr,
                                    workSchedule = schedule,
                                    serviceIds = if (role == AdminStaffRole.TRAINER) {
                                        selectedServiceIds.toList()
                                    } else {
                                        emptyList()
                                    },
                                ),
                                photo = pendingPhoto,
                                removePhoto = removePhoto,
                            ),
                        )
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    if (saving) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(18.dp),
                            color = AdminColors.OnBrand,
                            strokeWidth = 2.dp,
                        )
                    } else {
                        Text("Guardar")
                    }
                }
            }
        }
    }
}

@Composable
private fun HybridWorkTimeField(
    label: String,
    value: String,
    options: List<String>,
    enabled: Boolean,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    var menuOpen by remember { mutableStateOf(false) }
    Box(modifier) {
        OutlinedTextField(
            value = value,
            onValueChange = { v ->
                onValueChange(v.filter { ch -> ch.isDigit() || ch == ':' }.take(5))
            },
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
            label = { Text(label) },
            placeholder = { Text("08:00") },
            enabled = enabled,
            trailingIcon = {
                IconButton(
                    onClick = { if (enabled) menuOpen = true },
                    enabled = enabled,
                ) {
                    Icon(
                        Icons.Outlined.ExpandMore,
                        contentDescription = "Elegir hora",
                        tint = AdminColors.TextMuted,
                    )
                }
            },
            shape = RoundedCornerShape(10.dp),
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = AdminColors.Primary,
                unfocusedBorderColor = AdminColors.Outline,
            ),
        )
        DropdownMenu(
            expanded = menuOpen,
            onDismissRequest = { menuOpen = false },
            modifier = Modifier.heightIn(max = 280.dp),
        ) {
            options.forEach { opt ->
                DropdownMenuItem(
                    text = { Text(opt) },
                    onClick = {
                        onValueChange(opt)
                        menuOpen = false
                    },
                )
            }
        }
    }
}

@Composable
private fun WorkerFormLabel(text: String) {
    Text(
        text,
        fontSize = 13.sp,
        fontWeight = FontWeight.SemiBold,
        color = AdminColors.Text,
        modifier = Modifier.padding(bottom = 6.dp),
    )
}

@Composable
private fun WorkerFormField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    isPassword: Boolean = false,
    isError: Boolean = false,
    focusRequester: FocusRequester? = null,
    onNext: (() -> Unit)? = null,
    onPrev: (() -> Unit)? = null,
    imeAction: ImeAction = ImeAction.Next,
) {
    val (visible, toggleVisible) = rememberPasswordVisible()
    fun goNext() {
        onNext?.invoke()
    }
    fun goPrev() {
        onPrev?.invoke()
    }
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = Modifier
            .fillMaxWidth()
            .then(if (focusRequester != null) Modifier.focusRequester(focusRequester) else Modifier)
            .onPreviewKeyEvent { event ->
                if (event.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                when (event.key) {
                    Key.Enter, Key.NumPadEnter -> {
                        goNext()
                        true
                    }
                    Key.Tab -> {
                        if (event.isShiftPressed) goPrev() else goNext()
                        true
                    }
                    Key.DirectionDown -> {
                        goNext()
                        true
                    }
                    Key.DirectionUp -> {
                        goPrev()
                        true
                    }
                    else -> false
                }
            },
        singleLine = true,
        isError = isError,
        placeholder = { Text(placeholder, color = AdminColors.TextMuted) },
        visualTransformation = if (isPassword && !visible) PasswordVisualTransformation() else VisualTransformation.None,
        trailingIcon = if (isPassword) {
            { PasswordVisibilityToggle(visible = visible, onToggle = toggleVisible) }
        } else {
            null
        },
        keyboardOptions = KeyboardOptions(imeAction = imeAction),
        keyboardActions = KeyboardActions(
            onNext = { goNext() },
            onDone = { goNext() },
        ),
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
            errorBorderColor = AdminColors.Error,
        ),
    )
}
